<template>
  <AppSheet
    v-model="isOpen"
    :title="$t('review.title')"
    :subtitle="subtitle"
    @close="onClose"
  >
    <ReviewDeck
      v-if="snapshot.length || opened"
      :cards="snapshot"
      :categories="categories"
      :accounts="accounts"
      :suggestion-index="suggestionIndex"
      :busy="busy"
      @apply="onApply"
      @skip="onSkip"
      @finish="close"
    />
  </AppSheet>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "../../composables/useToast"
import { firestoreState, updateExpense, updateUserPreferences } from "../../stores/firestoreStore"
import type { FirestoreExpense } from "../../types/firestore"
import { buildMerchantSuggestionIndex, buildReviewQueue, type ReviewCard } from "../../utils/reviewQueue"
import { AppSheet } from "../ui"
import ReviewDeck from "./ReviewDeck.vue"

/**
 * Host for the weekly review deck. Owns every write so a failure is handled
 * once, and owns the snapshot so the deck can't reshuffle mid-session.
 */
const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: "update:modelValue", value: boolean): void }>()

const { t } = useI18n()
const toast = useToast()

const busy = ref(false)
const opened = ref(false)
const snapshot = ref<ReviewCard[]>([])
const handledCount = ref(0)

const categories = computed(() => firestoreState.categories.value)
const accounts = computed(() => firestoreState.accounts.value)
const suggestionIndex = computed(() => buildMerchantSuggestionIndex(firestoreState.expenses.value, categories.value))

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
})

const subtitle = computed(() =>
  snapshot.value.length ? t("review.subtitle", { count: snapshot.value.length }) : t("review.subtitle_empty"),
)

// Freeze the queue on open. Recomputing it live would renumber the deck under
// the user's finger on every write — and in a household, on the other member's
// writes too, arriving via the 60s refetch poll.
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    snapshot.value = buildReviewQueue(firestoreState.expenses.value, categories.value).cards
    handledCount.value = 0
    opened.value = true
  },
  { immediate: true },
)

async function onApply(payload: { expense: FirestoreExpense; updates: Partial<FirestoreExpense>; toastKey: string }) {
  busy.value = true
  try {
    await updateExpense(payload.expense.id, payload.updates)
    handledCount.value += 1
    toast.success(t(payload.toastKey))
  } catch {
    // The row may have been deleted by another household member between the
    // snapshot and this write. The deck has already moved on, which is the
    // right outcome either way — say so and don't block the session.
    toast.error(t("review.toast.failed"))
  } finally {
    busy.value = false
  }
}

function onSkip(_expense: FirestoreExpense) {
  // Nothing is written: a skipped row stays undecided on purpose, so it comes
  // back next week. The deck advances on its own.
}

function close() {
  isOpen.value = false
}

function onClose() {
  // Stamp the ritual's clock only when something was actually decided —
  // opening the sheet and closing it again is not a review, and pretending it
  // was would make the dashboard card lie about when you last sat down.
  if (handledCount.value > 0) {
    void updateUserPreferences({ lastReviewedAt: new Date().toISOString() }).catch(() => {
      // Preference writes are queued by the offline outbox; a hard failure
      // here costs a timestamp, not the user's work.
    })
  }
  opened.value = false
}
</script>
